import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Loader2, Mail, Lock, ArrowLeft, KeyRound, ShieldCheck } from "lucide-react";
import { useTutorAuth } from "../context/TutorAuthContext";
import { tutorApi, formatApiError } from "../lib/tutorApi";

// V25 — Tutor login page with embedded forgot-password (OTP → reset) flow.
// Mode state-machine: "login" → "forgot" → "otp" → "reset" → back to "login".

export default function TutorLogin() {
    const { tutor, login, portalEnabled, refreshPortalStatus } = useTutorAuth();
    const navigate = useNavigate();

    const [mode, setMode] = useState("login");
    const [form, setForm] = useState({ email: "", password: "", otp: "", new_password: "", confirm_password: "" });
    const [resetToken, setResetToken] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [pendingDetail, setPendingDetail] = useState(null); // 403 detail from server
    const [blockedDetail, setBlockedDetail] = useState(null);

    useEffect(() => {
        if (tutor && portalEnabled) navigate("/tutor/dashboard", { replace: true });
    }, [tutor, portalEnabled, navigate]);

    useEffect(() => {
        if (portalEnabled === false) {
            navigate("/tutor/unavailable", { replace: true });
        }
    }, [portalEnabled, navigate]);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const doLogin = async (e) => {
        e.preventDefault();
        if (!form.email || !form.password) return toast.error("Enter email and password");
        setSubmitting(true);
        setPendingDetail(null);
        setBlockedDetail(null);
        try {
            const t = await login(form.email.trim(), form.password);
            toast.success(t?.is_first_login ? "Welcome to Anand Tutorials" : "Welcome back");
            navigate(t?.must_change_password ? "/tutor/dashboard?force_pwd=1" : "/tutor/dashboard");
        } catch (err) {
            // If portal got disabled mid-session, refresh status
            if (err.response?.status === 503) {
                await refreshPortalStatus();
                navigate("/tutor/unavailable", { replace: true });
                return;
            }
            const detail = err.response?.data?.detail || "";
            // V25 — surface friendly inline panels for pending / blocked accounts
            if (err.response?.status === 403 && /blocked/i.test(detail)) {
                setBlockedDetail(detail);
                return;
            }
            if (err.response?.status === 403 && /Become a Tutor|admin approval|not active/i.test(detail)) {
                setPendingDetail(detail);
                return;
            }
            toast.error(formatApiError(detail) || err.message);
        } finally { setSubmitting(false); }
    };

    const doForgot = async (e) => {
        e.preventDefault();
        if (!form.email) return toast.error("Enter your registered email");
        setSubmitting(true);
        try {
            await tutorApi.post("/tutor/auth/forgot-password", { email: form.email.trim() });
            toast.success("If an account exists for that email, an OTP has been sent.");
            setMode("otp");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    const doVerifyOtp = async (e) => {
        e.preventDefault();
        if (!form.otp || form.otp.length < 4) return toast.error("Enter the 6-digit OTP from your email");
        setSubmitting(true);
        try {
            const { data } = await tutorApi.post("/tutor/auth/verify-otp", { email: form.email.trim(), otp: form.otp.trim() });
            setResetToken(data.reset_token);
            setMode("reset");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    const doReset = async (e) => {
        e.preventDefault();
        if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters");
        if (form.new_password !== form.confirm_password) return toast.error("Passwords do not match");
        setSubmitting(true);
        try {
            await tutorApi.post("/tutor/auth/reset-password", { reset_token: resetToken, new_password: form.new_password });
            toast.success("Password updated. Please log in with your new password.");
            setMode("login");
            setForm({ ...form, password: "", otp: "", new_password: "", confirm_password: "" });
            setResetToken("");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500";

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 flex items-center justify-center px-4 py-8">
            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="tutor-login-shell">
                <div className="bg-gradient-to-r from-[#1E3A8A] to-[#1E40AF] text-white p-6">
                    <div className="flex items-center gap-2 mb-2">
                        <GraduationCap className="w-5 h-5" />
                        <span className="text-[11px] uppercase tracking-[0.2em] opacity-80">Anand Tutorials</span>
                    </div>
                    <h1 className="text-2xl font-bold leading-tight">
                        {mode === "login" && "Tutor Portal Login"}
                        {mode === "forgot" && "Forgot your password?"}
                        {mode === "otp" && "Verify your email"}
                        {mode === "reset" && "Set a new password"}
                    </h1>
                    <p className="text-xs opacity-80 mt-1">
                        {mode === "login" && "Sign in to manage your tuitions, attendance and demo classes."}
                        {mode === "forgot" && "We&rsquo;ll email you a 6-digit one-time code valid for 10 minutes."}
                        {mode === "otp" && `Enter the 6-digit code we sent to ${form.email}.`}
                        {mode === "reset" && "Choose a strong new password (min 8 characters)."}
                    </p>
                </div>

                <div className="p-6 space-y-4">
                    {pendingDetail && mode === "login" && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900 space-y-2" data-testid="tutor-login-pending-panel">
                            <div className="font-bold">New to Anand Tutorials?</div>
                            <p className="text-xs leading-relaxed">{pendingDetail}</p>
                            <Link to="/become-a-tutor" data-testid="tutor-login-become-tutor-cta"
                                className="inline-flex items-center gap-1 text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-lg active:scale-95 transition">
                                Become a Tutor
                            </Link>
                        </div>
                    )}
                    {blockedDetail && mode === "login" && (
                        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 text-sm text-rose-900" data-testid="tutor-login-blocked-panel">
                            <div className="font-bold">Your Profile Has Been Blocked</div>
                            <p className="text-xs leading-relaxed mt-1">{blockedDetail}</p>
                        </div>
                    )}
                    {mode === "login" && !pendingDetail && !blockedDetail && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-[11px] text-blue-900 leading-relaxed" data-testid="tutor-login-default-pwd-hint">
                            <strong>First time here?</strong> Your default login password is the first 4
                            <span className="font-mono"> CAPITAL </span>
                            letters of your name followed by the last 4 digits of your Aadhaar number.
                            After login, please change your password.
                        </div>
                    )}
                    {mode === "login" && !pendingDetail && !blockedDetail && (
                        <div
                            className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-[11px] text-slate-700 leading-relaxed"
                            data-testid="tutor-login-direct-url-hint"
                        >
                            If you cannot see the Tutor Login button on the website, you can still log
                            in using your registered email and default password through the direct
                            Tutor Login URL provided by Anand Tutorials.
                        </div>
                    )}
                    {mode === "login" && (
                        <form onSubmit={doLogin} className="space-y-3" data-testid="tutor-login-form">
                            <Field icon={<Mail className="w-4 h-4 text-slate-400" />} label="Email">
                                <input type="email" value={form.email} onChange={update("email")} autoComplete="username"
                                    data-testid="tutor-login-email" className={inputCls} placeholder="you@example.com" />
                            </Field>
                            <Field icon={<Lock className="w-4 h-4 text-slate-400" />} label="Password">
                                <input type="password" value={form.password} onChange={update("password")} autoComplete="current-password"
                                    data-testid="tutor-login-password" className={inputCls} placeholder="••••••••" />
                            </Field>
                            <button type="submit" disabled={submitting}
                                data-testid="tutor-login-submit"
                                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60 flex items-center justify-center gap-2">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Sign in
                            </button>
                            <button type="button" onClick={() => setMode("forgot")} data-testid="tutor-login-forgot"
                                className="w-full text-xs text-[#1E3A8A] hover:underline text-center pt-1">
                                Forgot password?
                            </button>
                        </form>
                    )}

                    {mode === "forgot" && (
                        <form onSubmit={doForgot} className="space-y-3" data-testid="tutor-forgot-form">
                            <Field icon={<Mail className="w-4 h-4 text-slate-400" />} label="Registered email">
                                <input type="email" value={form.email} onChange={update("email")} required
                                    data-testid="tutor-forgot-email" className={inputCls} placeholder="you@example.com" />
                            </Field>
                            <button type="submit" disabled={submitting} data-testid="tutor-forgot-submit"
                                className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60 flex items-center justify-center gap-2">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Send OTP
                            </button>
                            <button type="button" onClick={() => setMode("login")} data-testid="tutor-forgot-back"
                                className="w-full text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1">
                                <ArrowLeft className="w-3 h-3" /> Back to login
                            </button>
                        </form>
                    )}

                    {mode === "otp" && (
                        <form onSubmit={doVerifyOtp} className="space-y-3" data-testid="tutor-otp-form">
                            <Field icon={<KeyRound className="w-4 h-4 text-slate-400" />} label="6-digit code">
                                <input type="text" inputMode="numeric" pattern="\d*" maxLength={6} value={form.otp}
                                    onChange={update("otp")} data-testid="tutor-otp-input"
                                    className={`${inputCls} text-center tracking-[0.6em] font-mono text-lg`} placeholder="••••••" />
                            </Field>
                            <button type="submit" disabled={submitting} data-testid="tutor-otp-submit"
                                className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60 flex items-center justify-center gap-2">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Verify OTP
                            </button>
                            <button type="button" onClick={() => { setMode("forgot"); setForm({ ...form, otp: "" }); }} data-testid="tutor-otp-resend"
                                className="w-full text-xs text-slate-500 hover:text-slate-700 inline-flex items-center justify-center gap-1">
                                <ArrowLeft className="w-3 h-3" /> Try a different email
                            </button>
                        </form>
                    )}

                    {mode === "reset" && (
                        <form onSubmit={doReset} className="space-y-3" data-testid="tutor-reset-form">
                            <Field icon={<Lock className="w-4 h-4 text-slate-400" />} label="New password (min 8 chars)">
                                <input type="password" value={form.new_password} onChange={update("new_password")}
                                    data-testid="tutor-reset-new" className={inputCls} placeholder="••••••••" />
                            </Field>
                            <Field icon={<Lock className="w-4 h-4 text-slate-400" />} label="Confirm new password">
                                <input type="password" value={form.confirm_password} onChange={update("confirm_password")}
                                    data-testid="tutor-reset-confirm" className={inputCls} placeholder="••••••••" />
                            </Field>
                            <button type="submit" disabled={submitting} data-testid="tutor-reset-submit"
                                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60 flex items-center justify-center gap-2">
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Update password
                            </button>
                        </form>
                    )}

                    <div className="text-center pt-2">
                        <Link to="/become-a-tutor" className="text-xs text-slate-500 hover:text-slate-700" data-testid="tutor-login-apply-link">
                            New to Anand Tutorials? Apply as a tutor →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Field({ icon, label, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 inline-flex items-center gap-1.5">
                {icon}{label}
            </span>
            {children}
        </label>
    );
}
